import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AdminLogo from "@/components/AdminLogo";
import { Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/v2/auth/forgot-password", { email: email.trim() });
      setSent(data);
      if (data.ok) toast.success(t("auth.forgot_check_email", "If that email exists, a reset link was sent."));
      else toast.warning(t("auth.forgot_not_configured", "Email service is not yet configured. Please contact your Super Admin for a manual reset."));
    } catch (err) {
      setSent(null);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Unable to send reset email. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }} />
      <div className="w-full max-w-md p-9 bg-zinc-900/85 backdrop-blur-2xl rounded-[28px] border border-zinc-800/80 shadow-2xl relative">
        <div className="flex items-center gap-3 mb-8">
          <AdminLogo size={44} variant="light" />
          <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-medium">Adelina · Employment Agency</div>
        </div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#d4a017] font-semibold mb-2">
          {t("auth.forgot_overline", "Forgot Password")}
        </div>
        <h1 className="font-serif text-white text-3xl mb-6 tracking-tight">
          {t("auth.forgot_title", "Reset your password")}
        </h1>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("auth.email", "Email")}</Label>
            <Input type="email" name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" required data-testid="forgot-email" />
          </div>
          <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60 flex items-center justify-center gap-2" data-testid="forgot-submit">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? t("auth.forgot_sending", "Sending…") : t("auth.forgot_send", "Send reset link")}
          </button>
        </form>
        {sent && !sent.ok && (
          <div className="mt-6 p-4 rounded-2xl bg-amber-900/20 border border-amber-800/60 text-amber-200 text-sm">
            {t("auth.forgot_not_configured", "Email service is not yet configured. Please contact your Super Admin for a manual reset.")}
          </div>
        )}
        <div className="mt-6 text-sm text-zinc-500">
          <Link to="/admin/login" className="hover:text-[#d4a017] transition-colors">
            {t("auth.back_signin", "← Back to sign in")}
          </Link>
        </div>
      </div>
    </div>
  );
}
